import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, ArrowUpward as ArrowUpwardIcon, Close as CloseIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BuilderActivityPanel } from '../components/admin/BuilderActivityPanel';
import {
  AnnouncementSeverity,
  FeatureKey,
  ModelOption,
  PlatformSettings,
  SystemSettingsPayload,
  adminSystemApi,
} from '../services/systemApi';

const SEVERITIES: AnnouncementSeverity[] = ['info', 'success', 'warning', 'error'];

const FEATURE_HELP: Record<FeatureKey, string> = {
  build_guide: 'The Build Guide chat. When off, its nav item is hidden and the dashboard box opens the Function Builder instead.',
  chain_builder: 'AI chain design and prompt cleanup in the Chain Builder. Building chains by hand still works.',
  object_generator: 'AI-designed output formats in the Object Generator. Editing fields by hand still works.',
  function_generator: 'The Generate prompts and Generate examples calls in the Function Builder.',
  app_builder: 'New messages in Studio App Builder sessions. Studio chat still works.',
};

const SectionCard: React.FC<{ title: string; subtitle?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Card variant="outlined" sx={{ mb: 2.5 }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const ModelSelect: React.FC<{ value: string; options: ModelOption[]; onChange: (v: string) => void; label: string }> = ({
  value,
  options,
  onChange,
  label,
}) => (
  <TextField select size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 300 }}>
    {options.map((m) => (
      <MenuItem key={m.id} value={m.id}>
        {m.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{m.provider}</Typography>
      </MenuItem>
    ))}
  </TextField>
);

const ModelStackEditor: React.FC<{
  value: string[];
  options: ModelOption[];
  max: number;
  onChange: (v: string[]) => void;
}> = ({ value, options, max, onChange }) => {
  const set = (i: number, id: string) => onChange(value.map((m, j) => (j === i ? id : m)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const moveUp = (i: number) => {
    const next = [...value];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const unused = options.find((o) => !value.includes(o.id));
  return (
    <Stack spacing={1}>
      {value.map((m, i) => (
        <Stack key={`${i}-${m}`} direction="row" spacing={1} alignItems="center">
          <ModelSelect value={m} options={options} onChange={(v) => set(i, v)} label={i === 0 ? 'Primary' : `Fallback ${i}`} />
          {i > 0 && (
            <Tooltip title="Try this one earlier">
              <IconButton size="small" onClick={() => moveUp(i)} aria-label="Move up">
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {value.length > 1 && (
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => remove(i)} aria-label="Remove">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ))}
      {value.length < max && unused && (
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...value, unused.id])}>
            Add fallback
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export const AdminSystemSettings: React.FC = () => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => adminSystemApi(getIdToken), [getIdToken]);
  const [payload, setPayload] = useState<SystemSettingsPayload | null>(null);
  const [draft, setDraft] = useState<PlatformSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'activity' ? 'activity' : 'settings';
  const setTab = (next: string) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'activity') p.set('tab', 'activity');
        else p.delete('tab');
        return p;
      },
      { replace: true },
    );
  const loadActivity = useCallback((days: number) => api.activity(days), [api]);

  const load = useCallback(() => {
    api
      .get()
      .then((p) => {
        setPayload(p);
        setDraft(p.settings);
      })
      .catch((e) => setError(e.message));
  }, [api]);

  useEffect(load, [load]);

  const dirty = useMemo(
    () => !!payload && !!draft && JSON.stringify(payload.settings) !== JSON.stringify(draft),
    [payload, draft],
  );

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const p = await api.save(draft);
      setPayload(p);
      setDraft(p.settings);
      setSavedAt(Date.now());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!payload || !draft) {
    return error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={24} />;
  }

  const byName = (a: ModelOption, b: ModelOption) => a.provider.localeCompare(b.provider) || a.name.localeCompare(b.name);
  const options = [...payload.model_options].sort(byName);
  const knowledgeOptions = [...(payload.knowledge_model_options || payload.model_options)].sort(byName);
  const patch = <K extends keyof PlatformSettings>(key: K, value: Partial<PlatformSettings[K]>) =>
    setDraft((d) => (d ? { ...d, [key]: { ...(d[key] as object), ...value } } : d));
  const a = draft.announcement;
  const b = draft.billing;
  const access = draft.access;
  const kp = draft.knowledge_preprocess;
  const lim = payload.limits;
  const modelNames = new Map(payload.model_options.map((m) => [m.id, m.name]));
  const modelName = (id: string | null) => (id ? modelNames.get(id) || id : '—');

  const saveBar = (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
      <Button variant="contained" onClick={save} disabled={!dirty || saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
      <Button onClick={() => setDraft(payload.settings)} disabled={!dirty || saving}>
        Discard
      </Button>
      <Typography variant="caption" color="text.secondary">
        {dirty
          ? 'Unsaved changes'
          : savedAt
            ? 'Saved. Every server picks it up within about 15 seconds; the Activity tab shows which models answer from then on.'
            : payload.updated_at
              ? `Last changed ${new Date(payload.updated_at + 'Z').toLocaleString()} by ${payload.updated_by || 'unknown'}`
              : 'Using built-in defaults'}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography variant="h5" gutterBottom>
        System Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Platform-wide settings that take effect without a release. Blank or invalid values fall back to the built-in defaults.
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="settings" label="Settings" />
        <Tab value="activity" label="Activity" />
      </Tabs>
      {tab === 'activity' ? (
        <BuilderActivityPanel load={loadActivity} modelName={modelName} />
      ) : (
      <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saveBar}

      <SectionCard
        title="Builder AI models"
        subtitle="The models EasyAI's own AI builders use. Each builder tries its primary model first and moves down the list when a model is busy or unavailable."
      >
        <Stack spacing={3}>
          {Object.entries(payload.model_features).map(([feature, label]) => {
            const isStack = payload.stack_features.includes(feature);
            const value = draft.models[feature];
            const def = payload.defaults.models[feature];
            const isDefault = JSON.stringify(value) === JSON.stringify(def);
            return (
              <Box key={feature}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                  {!isDefault && (
                    <Link component="button" type="button" variant="caption" onClick={() => patch('models', { [feature]: def })}>
                      Reset to default
                    </Link>
                  )}
                </Stack>
                {isStack ? (
                  <ModelStackEditor
                    value={value as string[]}
                    options={options}
                    max={payload.max_stack_models}
                    onChange={(v) => patch('models', { [feature]: v })}
                  />
                ) : (
                  <ModelSelect value={value as string} options={options} onChange={(v) => patch('models', { [feature]: v })} label="Model" />
                )}
              </Box>
            );
          })}
        </Stack>
        <Alert severity="info" variant="outlined" sx={{ mt: 3 }}>
          Your platform functions use the model set on each function in the Function Builder. The Function Builder's
          "Create with AI" and "Change it with AI" run the public functions <code>improve_aifunction</code> and{' '}
          <code>predictsysteminstructions</code>, so you change their models in the Function Builder in public mode. Self-improvement
          models are on the <RouterLink to="/dashboard/admin/learning">Self-improvement</RouterLink> page.
        </Alert>
      </SectionCard>

      <SectionCard
        title="Builders on/off"
        subtitle="Switch off an AI builder during an incident or a model outage. This only stops new AI design requests: existing functions and chains keep running. Platform admins can still use a switched-off builder to test it."
      >
        <Stack spacing={1.5}>
          {(Object.keys(payload.feature_switches) as FeatureKey[]).map((key) => (
            <Box key={key}>
              <FormControlLabel
                control={<Switch checked={!!draft.features[key]} onChange={(e) => patch('features', { [key]: e.target.checked })} />}
                label={<Typography sx={{ fontWeight: 600 }}>{payload.feature_switches[key]}</Typography>}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
                {FEATURE_HELP[key]}
              </Typography>
            </Box>
          ))}
        </Stack>
      </SectionCard>

      <SectionCard
        title="Knowledge Hub AI organizing"
        subtitle={
          <>
            When people add files to the Knowledge Hub, they can have an AI rewrite each file into self-contained, titled snippets
            (with page references) before it's made searchable. It runs the public function <code>knowledge_preprocessor</code>, whose
            instructions you can edit in the Function Builder in public mode. Unlike the builders, it is billed to the customer's
            credits. Parts the AI gets wrong, or that would go over the cap, use the standard split instead, so nothing is lost.
          </>
        }
      >
        <Stack spacing={2.5}>
          <Box>
            <FormControlLabel
              control={<Switch checked={kp.enabled} onChange={(e) => patch('knowledge_preprocess', { enabled: e.target.checked })} />}
              label={<Typography sx={{ fontWeight: 600 }}>{kp.enabled ? 'Available to customers' : 'Turned off'}</Typography>}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
              When off, the upload option is hidden and new uploads use the standard split. Files already organized keep their snippets.
            </Typography>
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Models</Typography>
              {JSON.stringify(kp.models) !== JSON.stringify(payload.defaults.knowledge_preprocess.models) && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => patch('knowledge_preprocess', { models: payload.defaults.knowledge_preprocess.models })}
                >
                  Reset to default
                </Link>
              )}
            </Stack>
            <ModelStackEditor
              value={kp.models}
              options={knowledgeOptions}
              max={payload.max_stack_models}
              onChange={(v) => patch('knowledge_preprocess', { models: v })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Each part of a file goes to the primary model. If its reply leaves out too much of the original, the same model gets one
              more try and is told what it dropped; if the reply is unusable (error, timeout, bad format), the first fallback gets that
              try. Customers pay for these calls, so a fast, inexpensive model is usually right, and "(fast)" variants skip the
              model's thinking, which is billed as output.
            </Typography>
          </Box>
          <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
            <TextField
              size="small"
              type="number"
              label="On by default for documents up to"
              value={kp.default_on_max_mb}
              inputProps={{ min: lim.knowledge_default_on_mb_range[0], max: lim.knowledge_default_on_mb_range[1], step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">MB</InputAdornment> }}
              onChange={(e) => patch('knowledge_preprocess', { default_on_max_mb: Number(e.target.value) })}
              helperText="PDF, Word, PowerPoint, HTML, Markdown and text files up to this size have the option ticked in the upload dialog. Spreadsheets, CSV and JSON start unticked. People can always change it."
            />
            <TextField
              size="small"
              type="number"
              label="Largest file to AI-organize"
              value={kp.max_chars_per_file}
              inputProps={{ min: lim.knowledge_max_chars_range[0], max: lim.knowledge_max_chars_range[1], step: 10000 }}
              InputProps={{ endAdornment: <InputAdornment position="end">characters</InputAdornment> }}
              onChange={(e) => patch('knowledge_preprocess', { max_chars_per_file: Number(e.target.value) })}
              helperText="Counted on the text read from the file (roughly 2,000 to 3,000 characters per page). Larger files use the standard split."
            />
            <TextField
              size="small"
              type="number"
              label="Credit cap per file"
              value={kp.max_credits_per_file}
              inputProps={{ min: lim.knowledge_max_credits_range[0], max: lim.knowledge_max_credits_range[1], step: 10 }}
              InputProps={{ endAdornment: <InputAdornment position="end">credits</InputAdornment> }}
              onChange={(e) => patch('knowledge_preprocess', { max_credits_per_file: Number(e.target.value) })}
              helperText="A file whose estimate is over this isn't AI-organized. If a run gets close to it, the remaining parts use the standard split."
            />
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Announcement"
        subtitle="A banner at the top of every portal page, for maintenance notices or launches. People can dismiss it; changing the message shows it again."
      >
        <Stack spacing={2}>
          <FormControlLabel
            control={<Switch checked={a.enabled} onChange={(e) => patch('announcement', { enabled: e.target.checked })} />}
            label="Show the announcement"
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="Style"
              value={a.severity}
              onChange={(e) => patch('announcement', { severity: e.target.value as AnnouncementSeverity })}
              sx={{ minWidth: 160 }}
            >
              {SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s[0].toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Button text (optional)"
              value={a.link_label}
              inputProps={{ maxLength: 40 }}
              onChange={(e) => patch('announcement', { link_label: e.target.value })}
            />
            <TextField
              size="small"
              fullWidth
              label="Button link (optional)"
              placeholder="/dashboard/guide or https://…"
              value={a.link_url}
              onChange={(e) => patch('announcement', { link_url: e.target.value })}
            />
          </Stack>
          <TextField
            label="Message"
            multiline
            minRows={2}
            value={a.message}
            inputProps={{ maxLength: lim.announcement_max_chars }}
            helperText={`${a.message.length}/${lim.announcement_max_chars}`}
            onChange={(e) => patch('announcement', { message: e.target.value })}
          />
          {a.message.trim() && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Preview
              </Typography>
              <Alert
                severity={a.severity}
                action={a.link_label && a.link_url ? <Button color="inherit" size="small">{a.link_label}</Button> : undefined}
                sx={{ alignItems: 'center' }}
              >
                {a.message}
              </Alert>
            </Box>
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="Credits" subtitle="Plan prices and tiers come from Stripe and the pricing code, not from here.">
        <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
          <TextField
            size="small"
            type="number"
            label="Free credits for new signups"
            value={b.signup_credits}
            inputProps={{ min: lim.signup_credits_range[0], max: lim.signup_credits_range[1], step: 100 }}
            onChange={(e) => patch('billing', { signup_credits: Number(e.target.value) })}
            helperText={`Granted once when a new account signs up, and shown on the landing page (${lim.signup_credits_range[0].toLocaleString()} to ${lim.signup_credits_range[1].toLocaleString()}).`}
          />
          <TextField
            size="small"
            type="number"
            label="Overage allowed while an auto-refill is processing"
            value={b.credit_buffer_percent}
            inputProps={{ min: 0, max: lim.credit_buffer_percent_max, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            onChange={(e) => patch('billing', { credit_buffer_percent: Number(e.target.value) })}
            helperText={`When an account with auto-refill hits its limit, requests keep working up to this much over the new limit while the refill is charged (0 to ${lim.credit_buffer_percent_max}%).`}
          />
          <TextField
            size="small"
            type="number"
            label="Largest single auto-refill charge"
            value={b.max_single_refill_credits}
            inputProps={{ min: lim.max_refill_credits_range[0], max: lim.max_refill_credits_range[1], step: lim.refill_unit }}
            InputProps={{ endAdornment: <InputAdornment position="end">credits</InputAdornment> }}
            onChange={(e) => patch('billing', { max_single_refill_credits: Number(e.target.value) })}
            helperText={`A safety cap on one automatic refill, rounded up to a multiple of ${lim.refill_unit} (${lim.max_refill_credits_range[0].toLocaleString()} to ${lim.max_refill_credits_range[1].toLocaleString()}).`}
          />
        </Stack>
      </SectionCard>

      <SectionCard
        title="Limits"
        subtitle="Protects against runaway use of the builder AIs, which run on our most expensive models and aren't billed to the customer."
      >
        <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
          <TextField
            size="small"
            type="number"
            label="Builder AI requests per account per day"
            value={draft.limits.builder_daily_calls_per_account}
            inputProps={{ min: lim.builder_daily_cap_range[0], max: lim.builder_daily_cap_range[1], step: 10 }}
            onChange={(e) => patch('limits', { builder_daily_calls_per_account: Number(e.target.value) })}
            helperText="0 means no cap. Counts Build Guide messages, Chain Builder and Object Generator designs, and Generate prompts/examples, together. Resets at midnight UTC. Platform admins are never capped. Check typical use on the Activity tab before setting it; people over the cap see a friendly 'try again tomorrow' message."
          />
        </Stack>
      </SectionCard>

      <SectionCard
        title="Sign-ups"
        subtitle="Pause new account creation, for example during an incident or abuse wave. People who already have an account can still sign in and use everything."
      >
        <Stack spacing={2}>
          <FormControlLabel
            control={<Switch checked={access.signups_open} onChange={(e) => patch('access', { signups_open: e.target.checked })} />}
            label={access.signups_open ? 'New sign-ups are open' : 'New sign-ups are paused'}
          />
          <TextField
            label="Message shown on the sign-up page while paused"
            multiline
            minRows={2}
            value={access.signups_closed_message}
            placeholder={lim.default_signups_closed_message}
            inputProps={{ maxLength: lim.signups_closed_message_max_chars }}
            helperText={`Leave blank for the default. ${access.signups_closed_message.length}/${lim.signups_closed_message_max_chars}`}
            onChange={(e) => patch('access', { signups_closed_message: e.target.value })}
          />
          {!access.signups_open && (
            <Alert severity="warning">
              While paused, the sign-up form is replaced by this message and the sign-up API refuses new accounts.
            </Alert>
          )}
        </Stack>
      </SectionCard>

      {saveBar}
      </>
      )}
    </Box>
  );
};

export default AdminSystemSettings;
